/**
 * What a studio page says when one of its calls fails (Brand OS, Video Studio, Audio Studio, Deck Studio), built on
 * the shared launch answers (apiSignals.ts) and cap wording (caps.ts), so every studio and the app shell say the
 * same thing:
 * - a monthly cap reached (429 cap_reached): the one cap toast, the same one the shell's fetch observer shows
 *   (useLaunchSignals), so the person sees it once however many places noticed it;
 * - a studio switched off or every client paused (403): the closed / paused sentence of the calm studio page;
 * - any other 429 (an hourly AI limit, a busy render slot): that answer's own sentence, never the monthly-cap one;
 * - anything else: the error's own message.
 */
import { toast } from 'sonner';
import { accessClosedOf, capReachedOf, type AccessClosed, type CapReachedBody, type CapStudio } from './apiSignals';
import { STUDIO_NAMES, capReachedMessage } from './caps';
import { ASK_BEATLY, STUDIO_PAUSED_TITLE } from './StudioUnavailable';

export { accessClosedOf, capReachedOf, type AccessClosed };

const RATE_LIMITED = 'Too many requests right now. Try again in a few minutes.';

/** The sentence for a studio switched off for the team, or for every client paused. */
export function closedMessage(kind: AccessClosed, studio?: CapStudio | string): string {
  if (kind === 'clients_paused') return `${STUDIO_PAUSED_TITLE} ${ASK_BEATLY}`;
  const name = studio && STUDIO_NAMES[studio] ? STUDIO_NAMES[studio] : 'This studio';
  return `${name} isn't switched on for your team. ${ASK_BEATLY}`;
}

function bodyOf(err: unknown): Record<string, unknown> | null {
  const e = err && typeof err === 'object' ? (err as Record<string, unknown>) : null;
  const b = e && e.body && typeof e.body === 'object' ? (e.body as Record<string, unknown>) : null;
  if (!b) return null;
  return b.detail && typeof b.detail === 'object' && !Array.isArray(b.detail) ? (b.detail as Record<string, unknown>) : b;
}

/** A 429 that is not a monthly cap: the answer's own sentence (`message`, else its `error`), or a plain one. */
function busyTextOf(err: unknown): string | null {
  const e = err && typeof err === 'object' ? (err as { status?: unknown }) : null;
  const body = bodyOf(err);
  if (e?.status !== 429 && body?.error !== 'rate_limited') return null;
  const text = body && (typeof body.message === 'string' ? body.message : typeof body.error === 'string' ? body.error : null);
  return text && text !== 'rate_limited' ? text : RATE_LIMITED;
}

/** The text for a failed studio call, for an inline message: the cap, closed or busy sentence, else the error's own. */
export function studioErrorText(err: unknown, studio: CapStudio, otherwise = 'Something went wrong. Try again.'): string {
  const cap = capReachedOf(err);
  if (cap) return capReachedMessage(cap, studio);
  const closed = accessClosedOf(err);
  if (closed) return closedMessage(closed, studio);
  const busy = busyTextOf(err);
  if (busy) return busy;
  if (err instanceof Error && err.message === 'rate_limited') return RATE_LIMITED;
  return err instanceof Error && err.message ? err.message : otherwise;
}

const capToastAt = new Map<string, number>();

/**
 * The one toast for a reached cap, per studio and unit (sonner replaces a toast with the same id, so the shell's fetch
 * observer and the page that caught the error never show two). An answer without numbers (an older helper's bare
 * "cap_reached") stays quiet when this studio's cap was just shown with them.
 */
export function showCapToast(cap: CapReachedBody, fallbackStudio?: CapStudio | string): void {
  const studio = cap.studio || fallbackStudio || '';
  if (!cap.unit && Date.now() - (capToastAt.get(studio) ?? 0) < 10_000) return;
  capToastAt.set(studio, Date.now());
  toast(capReachedMessage(cap, studio), { id: `cap:${studio}:${cap.unit || 'any'}`, duration: 10_000 });
}

/**
 * Toast a failed studio call: a reached cap goes to the shared cap toast (and a page's own pending toast `id`, if it
 * passed one, is closed); everything else is an error toast with {@link studioErrorText}.
 */
export function toastStudioError(err: unknown, studio: CapStudio, otherwise?: string, opts?: { id?: string | number }): void {
  const cap = capReachedOf(err);
  if (cap) {
    if (opts?.id !== undefined) toast.dismiss(opts.id);
    showCapToast(cap, studio);
    return;
  }
  toast.error(studioErrorText(err, studio, otherwise), opts);
}
